import React, { useState } from "react";
import axios from "axios";
import { API_URL } from "../../config";
import { authHeaders, usd } from "./feeTiers";

export default function ChargeConfirmModal({ row, onCancel, onConfirmed }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const methodLabel =
    row.payment_method_type === "ach"
      ? `${row.payment_method_bank_name || "Bank"} ending in ${row.payment_method_last4 || "••••"}`
      : `Card ending in ${row.payment_method_last4 || "••••"}`;

  const snap = row.load_snapshot;
  const o = snap ? `${snap.oc || ""}, ${snap.os || ""}`.replace(/^,\s*|,\s*$/g, "") : "";
  const d = snap ? `${snap.dc || ""}, ${snap.ds || ""}`.replace(/^,\s*|,\s*$/g, "") : "";
  const lane = !o && !d ? "this load" : `${o || "?"} → ${d || "?"}`;

  const negotiatedDollars = Math.round((row.negotiated_rate_cents || 0) / 100);
  const postedDollars = row.posted_rate_cents ? Math.round(row.posted_rate_cents / 100) : null;
  // The backend's quote for this row's tier; the page never prices the fee itself.
  // 0 means it books on the carrier's free credit.
  const freeCredit = row.fee_preview_cents === 0;
  const feeLabel = row.fee_preview_cents != null ? usd(row.fee_preview_cents) : "the fee";

  async function confirm() {
    setBusy(true);
    setError("");
    try {
      await axios.post(
        `${API_URL}/book-it/admin/${row.booking_request_id}/booked`,
        {},
        { headers: authHeaders() }
      );
      onConfirmed();
    } catch (err) {
      setError(err?.response?.data?.detail || "Charge failed. Please try again.");
      setBusy(false);
    }
  }

  return (
    <div
      onClick={busy ? undefined : onCancel}
      className="fixed inset-0 z-[1000] flex items-center justify-center px-5 bg-slate-900/60 backdrop-blur-[2px]"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl p-7 w-full max-w-md shadow-2xl text-slate-900"
      >
        <h2 className="text-xl font-bold tracking-tight text-slate-900 mb-3">
          {freeCredit ? "Book on free credit?" : "Charge reservation fee?"}
        </h2>
        {freeCredit ? (
          <p className="text-sm text-slate-600 leading-relaxed mb-4">
            Book <strong className="font-semibold text-slate-900">{lane}</strong> on{" "}
            <strong className="font-semibold text-slate-900">{row.trucker_name}</strong>'s free
            credit (no fee). Nothing is charged to{" "}
            <strong className="font-semibold text-slate-900">{methodLabel}</strong>.
          </p>
        ) : (
          <p className="text-sm text-slate-600 leading-relaxed mb-4">
            Charge <strong className="font-semibold text-slate-900">{row.trucker_name}</strong> {feeLabel}{" "}
            (flat fee for ${negotiatedDollars.toLocaleString()} negotiated) to{" "}
            <strong className="font-semibold text-slate-900">{methodLabel}</strong> for booking{" "}
            <strong className="font-semibold text-slate-900">{lane}</strong>.
          </p>
        )}
        {!freeCredit && postedDollars && postedDollars !== negotiatedDollars && (
          <p className="text-xs text-slate-500 mb-4">
            Posted rate was ${postedDollars.toLocaleString()} — actual fee based on $
            {negotiatedDollars.toLocaleString()} negotiated per the uploaded rate
            confirmation.
          </p>
        )}
        <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-3.5 py-3 text-sm leading-relaxed mb-5">
          {freeCredit
            ? "Make sure the load is actually booked with the broker before marking it booked."
            : "Make sure the load is actually booked with the broker before charging. The carrier authorized this charge when they reserved the load."}
        </div>
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-3.5 py-2.5 text-sm mb-3">
            {error}
          </div>
        )}
        <div className="flex justify-end gap-2.5">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="!shadow-none !bg-white !text-slate-700 !border !border-slate-300 hover:!bg-slate-50 !rounded-lg !px-4 !py-2.5 !text-sm !font-semibold disabled:opacity-60 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={confirm}
            disabled={busy}
            className="!shadow-none !bg-indigo-600 hover:!bg-indigo-700 !text-white !border-0 !rounded-lg !px-4 !py-2.5 !text-sm !font-semibold disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {busy
              ? freeCredit
                ? "Booking…"
                : "Charging…"
              : freeCredit
                ? "Yes, mark booked (no fee)"
                : `Yes, charge ${feeLabel} and mark booked`}
          </button>
        </div>
      </div>
    </div>
  );
}
